// Pure helpers for choosing batches on a transfer. No React, no I/O — tested by
// frontend/tests/batchPicking.test.mjs. The server is the authority on every
// rule here (utils/pharmacyTransfer.js re-checks under row locks); these only
// make the screen agree with it before the request goes.

export interface PickableBatch {
  inventory_id: number;
  medicine_id: number;
  batch_number: string;
  expiry_date: string;
  quantity_available: number;
}

export interface PickedLine<B extends PickableBatch = PickableBatch> {
  batch: B;
  quantity: number;
}

const byExpiry = (a: PickableBatch, b: PickableBatch) =>
  (a.expiry_date < b.expiry_date ? -1 : a.expiry_date > b.expiry_date ? 1 : a.inventory_id - b.inventory_id);

/**
 * Earliest-expiry-first fill: for each needed medicine, take from its batches
 * soonest-expiring first until the need is met. `short` lists what the shelf
 * can't cover.
 */
export function pickBatches<B extends PickableBatch>(
  needs: Array<{ medicine_id: number; quantity: number }>,
  stock: B[],
): { lines: Array<PickedLine<B>>; short: Array<{ medicine_id: number; missing: number }> } {
  const lines: Array<PickedLine<B>> = [];
  const short: Array<{ medicine_id: number; missing: number }> = [];
  for (const need of needs) {
    let left = need.quantity;
    if (left <= 0) continue;
    const batches = stock.filter((b) => b.medicine_id === need.medicine_id && b.quantity_available > 0).sort(byExpiry);
    for (const b of batches) {
      if (left <= 0) break;
      const take = Math.min(left, b.quantity_available);
      lines.push({ batch: b, quantity: take });
      left -= take;
    }
    if (left > 0) short.push({ medicine_id: need.medicine_id, missing: left });
  }
  return { lines, short };
}

/**
 * The earlier-expiring batch of the same medicine that could have covered this
 * line instead — the case the server asks a reason for — or null. An earlier
 * batch only counts if enough of it is left after what the OTHER lines of this
 * transfer already take from it (the server's freeAdjustment), so filling a
 * quantity across batches in expiry order never asks for a reason.
 */
export function earlierBatchFor<B extends PickableBatch>(
  line: { batch: B; quantity: number },
  allLines: Array<{ batch: B; quantity: number }>,
  stock: B[],
): B | null {
  const qty = line.quantity > 0 ? line.quantity : 1;
  const claimed = (id: number) =>
    allLines.filter((l) => l !== line && l.batch.inventory_id === id).reduce((n, l) => n + (l.quantity > 0 ? l.quantity : 0), 0);
  return stock
    .filter((b) => b.medicine_id === line.batch.medicine_id && b.inventory_id !== line.batch.inventory_id
      && b.expiry_date < line.batch.expiry_date)
    .sort(byExpiry)
    .find((b) => b.quantity_available - claimed(b.inventory_id) >= qty) ?? null;
}
